import type { Product } from '@cartazo/contracts'
import { previewUnitPrice } from '@cartazo/core'
import { del, get as idbGet, set as idbSet } from 'idb-keyval'
import { create } from 'zustand'
import { track } from '../../lib/analytics'
import type { NormalizedMenu } from '../menu/normalize'

/**
 * Carrito del comensal.
 *
 * Persiste en IndexedDB y no en localStorage porque sobrevive mejor a la
 * presión de memoria de un móvil con muchas pestañas abiertas, que es la
 * situación normal de un teléfono en un restaurante.
 */

export type CartLine = {
  /** Identificador de la línea, no del producto: el mismo plato puede pedirse
   *  dos veces con modificadores distintos y son líneas separadas. */
  lineId: string
  productId: string
  name: string
  basePrice: number
  optionIds: string[]
  optionLabels: { group: string; option: string; priceExtra: number }[]
  quantity: number
  unitPrice: number
  note: string | null
  /**
   * Plato o bebida, copiado al añadir.
   *
   * Se guarda en la línea en vez de mirarlo en la carta al pintarla porque el
   * carrito sobrevive en IndexedDB a que la carta no esté cargada todavía, y
   * el resumen del garzón tiene que poder agruparse igual.
   */
  kind?: 'dish' | 'drink'
  /** Marcada cuando el plato desapareció de la carta al rehidratar. */
  removed?: boolean
  /** Precio anterior, si cambió desde que se añadió. */
  previousUnitPrice?: number
}

export type CartState = {
  slug: string | null
  tableLabel: string | null
  lines: CartLine[]
  /** Avisos que deben mostrarse al comensal tras rehidratar el carrito. */
  notices: string[]
  hydrated: boolean
}

export type CartActions = {
  init(slug: string, tableLabel: string | null): Promise<void>
  add(product: Product, optionIds: string[], quantity: number, note: string | null): void
  setQuantity(lineId: string, quantity: number): void
  remove(lineId: string): void
  clear(): Promise<void>
  dismissNotices(): void
  revalidate(menu: NormalizedMenu): void
}

/** Cuatro horas: más que cualquier comida, menos que "ayer". */
const TTL_MS = 4 * 60 * 60 * 1000

type Persisted = { savedAt: number; tableLabel: string | null; lines: CartLine[] }

const storageKey = (slug: string, table: string | null) => `cart:${slug}:${table ?? 'sin-mesa'}`

export const useCart = create<CartState & CartActions>((set, get) => ({
  slug: null,
  tableLabel: null,
  lines: [],
  notices: [],
  hydrated: false,

  async init(slug, tableLabel) {
    set({ slug, tableLabel })

    try {
      const stored = await idbGet<Persisted>(storageKey(slug, tableLabel))

      // Un carrito de anteayer no es un carrito: es basura que confundiría al
      // comensal y podría acabar en una comanda equivocada.
      if (stored && Date.now() - stored.savedAt < TTL_MS) {
        set({ lines: stored.lines, hydrated: true })
        return
      }
      if (stored) await del(storageKey(slug, tableLabel))
    } catch {
      // IndexedDB puede estar bloqueado en navegación privada. Se sigue sin
      // persistencia antes que no dejar pedir.
    }

    set({ hydrated: true })
  },

  add(product, optionIds, quantity, note) {
    // "Añade al carrito" es el paso del embudo que separa mirar de pedir.
    track('add_to_cart', {
      entity_type: 'product',
      entity_id: product.id,
      props: { quantity },
    })

    const unitPrice = previewUnitPrice(product, optionIds)

    const optionLabels = product.modifiers.flatMap((group) =>
      group.options
        .filter((option) => optionIds.includes(option.id))
        .map((option) => ({
          group: group.name,
          option: option.name,
          priceExtra: option.price_extra,
        })),
    )

    const signature = `${product.id}|${[...optionIds].sort().join(',')}|${note ?? ''}`
    const existing = get().lines.find((line) => lineSignature(line) === signature && !line.removed)

    // Añadir dos veces lo mismo incrementa la cantidad en vez de duplicar la
    // línea: un resumen con "1x Burger" repetido cinco veces es ilegible para
    // el garzón.
    if (existing) {
      get().setQuantity(existing.lineId, existing.quantity + quantity)
      return
    }

    const line: CartLine = {
      lineId: crypto.randomUUID(),
      productId: product.id,
      name: product.name,
      basePrice: product.price,
      optionIds: [...optionIds],
      optionLabels,
      quantity,
      unitPrice,
      note,
      kind: product.kind,
    }

    set({ lines: [...get().lines, line] })
    void persist(get())
  },

  setQuantity(lineId, quantity) {
    if (quantity <= 0) {
      get().remove(lineId)
      return
    }
    set({
      lines: get().lines.map((line) => (line.lineId === lineId ? { ...line, quantity } : line)),
    })
    void persist(get())
  },

  remove(lineId) {
    set({ lines: get().lines.filter((line) => line.lineId !== lineId) })
    void persist(get())
  },

  async clear() {
    const { slug, tableLabel } = get()
    set({ lines: [], notices: [] })
    if (slug) await del(storageKey(slug, tableLabel)).catch(() => {})
  },

  dismissNotices() {
    set({ notices: [] })
  },

  /**
   * Contrasta el carrito guardado con la carta actual.
   *
   * Es imprescindible: sin esto, un comensal que dejó el carrito abierto una
   * hora envía por WhatsApp un pedido con precios viejos, y el restaurante
   * cobra otra cosa de la que el cliente vio. Aquí se detecta y se le pregunta.
   */
  revalidate(menu) {
    const notices: string[] = []

    const lines = get().lines.map((line) => {
      const entry = menu.byProductId.get(line.productId)

      if (!entry) {
        notices.push(`"${line.name}" ya no está en la carta`)
        return { ...line, removed: true }
      }

      const product = entry.product

      if (!product.is_available) {
        notices.push(`"${product.name}" se agotó`)
        return { ...line, removed: true }
      }

      const currentPrice = previewUnitPrice(product, line.optionIds)
      if (currentPrice !== line.unitPrice) {
        notices.push(`El precio de "${product.name}" cambió`)
        return { ...line, previousUnitPrice: line.unitPrice, unitPrice: currentPrice }
      }

      return line
    })

    set({ lines, notices })
    void persist({ ...get(), lines })
  },
}))

function lineSignature(line: CartLine): string {
  return `${line.productId}|${[...line.optionIds].sort().join(',')}|${line.note ?? ''}`
}

async function persist(state: CartState): Promise<void> {
  if (!state.slug) return
  const payload: Persisted = {
    savedAt: Date.now(),
    tableLabel: state.tableLabel,
    lines: state.lines,
  }
  await idbSet(storageKey(state.slug, state.tableLabel), payload).catch(() => {})
}

// ── Selectores derivados ───────────────────────────────────────────────────
//
// REGLA: un selector que se pasa a `useCart` debe devolver un valor PRIMITIVO o
// una referencia estable. Devolver un array nuevo —por ejemplo, el resultado de
// un `.filter()`— hace que zustand vea un snapshot distinto en cada render y
// entre en un bucle infinito de actualizaciones.
//
// Por eso `activeLines` es una función normal que se aplica sobre `state.lines`
// ya seleccionado, y no un selector que se suscriba por su cuenta.

/** Líneas vigentes: excluye las retiradas al revalidar contra la carta. */
export const activeLines = (lines: CartLine[]) => lines.filter((line) => !line.removed)

export const selectItemCount = (state: CartState) =>
  activeLines(state.lines).reduce((sum, line) => sum + line.quantity, 0)

export const selectSubtotal = (state: CartState) =>
  activeLines(state.lines).reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)

/** Referencia estable: zustand solo notifica cuando el array cambia de verdad. */
export const selectLines = (state: CartState) => state.lines
